import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { Cta, Footer, ItemRow, JobTile, V1Screen } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { fmtDate, tr } from '@/features/gate/v1/text'
import { isRunLive, liveOutputs, type ProcessState, type RunView } from '../projection'
import { sampleCandidates } from '../model/rules'
import { k, PIcon, type PIconName } from './icons'
import { LeadBox, NextArrow, PHeader, stepLabel, tail2, TickDot } from './ui'
import { WithSite } from './parts'

/** The live run on this site, newest first (one machine operator, one run at a time on V1). */
export function liveRun(state: ProcessState): RunView | null {
  return (
    [...state.runs.values()]
      .filter((r) => isRunLive(r.status))
      .sort((a, b) => (b.opened_at ?? '').localeCompare(a.opened_at ?? ''))[0] ?? null
  )
}

/** Runs a supervisor can check (the balance flagged them). */
export const flaggedRuns = (state: ProcessState) =>
  [...state.runs.values()].filter((r) => r.status === 'EXCEPTION')

/** V1-F6-00 — आज: the run on the machine, its five steps, the sample and the run check. */
export function V1ProcessHome() {
  return <WithSite>{(state) => <Home state={state} />}</WithSite>
}

function Home({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const actor = useActor()
  const [now] = useState(() => Date.now())
  const run = liveRun(state)
  const machine = run ? state.machines.get(run.equipment_id) : undefined
  const samples = sampleCandidates(state)
  const flagged = flaggedRuns(state)
  const supervisor = hasAnyRole(actor.roles, [ROLES.SUPERVISOR])
  // Machine work (open a run, the lab sample): machine operator, supervisor.
  const operator = hasAnyRole(actor.roles, [ROLES.MACHINE_OPERATOR, ROLES.SUPERVISOR])
  // The full build's tiles: issue to a run (store keeper, supervisor); machine label (operator,
  // supervisor, central admin).
  const keeper = hasAnyRole(actor.roles, [ROLES.STORE_KEEPER, ROLES.SUPERVISOR])
  const labeller = hasAnyRole(actor.roles, [
    ROLES.MACHINE_OPERATOR,
    ROLES.SUPERVISOR,
    ROLES.CENTRAL_ADMIN,
  ])
  const issueTasks = [...state.issueTasks.values()].filter(
    (x) => x.status === 'LOADING' && !x.closed_local,
  ).length
  const unlabelled = [...state.machines.values()].filter((m) => !m.label_payload).length

  const steps: Array<{ icon: PIconName; label: string; done: boolean }> = [
    { icon: 'plus', label: tr('process.v1.home.step1'), done: !!run },
    { icon: 'broom', label: tr('process.v1.home.step2'), done: !!run?.cleanout_confirmed_at },
    { icon: 'hopper', label: tr('process.v1.home.step3'), done: (run?.inputs.length ?? 0) > 0 },
    {
      icon: 'machine',
      label: tr('process.v1.home.step4'),
      done: !!run && liveOutputs(run).length > 0,
    },
    { icon: 'scale', label: tr('process.v1.home.step5'), done: false },
  ]

  return (
    <V1Screen
      nav
      header={
        <PHeader
          icon="home"
          title={tr('process.v1.home.title', { date: fmtDate(now) })}
          sub={actor.name}
        />
      }
      footer={
        run ? (
          <Footer>
            <Cta
              label={tr('process.v1.home.continue')}
              icon="next"
              onClick={() => navigate(`/m/process/run/${run.id}`)}
            />
          </Footer>
        ) : operator ? (
          <Footer>
            <Cta
              label={tr('process.v1.home.open')}
              icon={k('plus')}
              lead
              onClick={() => navigate('/m/process/open')}
            />
          </Footer>
        ) : undefined
      }
    >
      <ItemRow
        padding="8px 10px"
        onClick={run ? () => navigate(`/m/process/run/${run.id}`) : undefined}
        border={run ? C.acc : C.rule}
        borderWidth={run ? 2 : 1.5}
        lead={<LeadBox icon="machine" bg={run ? C.acc : C.sunk} color={run ? '#ffffff' : C.acc} />}
        title={
          run
            ? tr('process.v1.home.runOpen', {
                code: tail2(run.code),
                machine: `${machine?.code ?? ''} · ${stepLabel(state, run.step_code)}`,
              })
            : tr('process.v1.home.noRun')
        }
        right={run ? <NextArrow /> : undefined}
      />
      <section
        style={{
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          borderRadius: 12,
          padding: '6px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {steps.map((s, i) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <b style={{ width: 18, fontSize: 17, color: C.muted }}>{i + 1}</b>
            <span style={{ color: s.done ? C.ok : C.acc, display: 'flex' }}>
              <PIcon name={s.icon} size={26} />
            </span>
            <span style={{ fontSize: 18, fontWeight: 600, flexGrow: 1 }}>{s.label}</span>
            {s.done && <TickDot size={22} />}
          </div>
        ))}
      </section>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        {operator && (
          <JobTile
            icon={k('scoop')}
            color={C.acc}
            title={tr('process.v1.home.sample')}
            line={samples.length ? tr('process.v1.home.sampleLine') : undefined}
            onClick={() => navigate('/m/process/qc-sample')}
          />
        )}
        {supervisor && (
          <JobTile
            icon="search"
            color={flagged.length ? C.bad : C.ink}
            title={tr('process.v1.home.check')}
            line={
              flagged.length
                ? tr('process.v1.home.checkLine', { count: flagged.length })
                : undefined
            }
            onClick={() => navigate('/m/process/investigate')}
          />
        )}
        {keeper && (
          <JobTile
            icon="sack"
            color={issueTasks ? C.acc : C.ink}
            title={tr('process.v1.issue.tile')}
            line={issueTasks ? tr('process.v1.issue.tileLine', { count: issueTasks }) : undefined}
            onClick={() => navigate('/m/process/issue')}
          />
        )}
        {keeper && (
          <JobTile
            icon="truck"
            color={C.ink}
            title={tr('process.v1.home.godown')}
            line={tr('process.v1.home.godownLine')}
            onClick={() => navigate('/m/store')}
          />
        )}
        {labeller && (
          <JobTile
            icon="tag"
            color={C.ink}
            title={tr('process.v1.machineLabel.tile')}
            line={
              unlabelled ? tr('process.v1.machineLabel.tileLine', { count: unlabelled }) : undefined
            }
            onClick={() => navigate('/m/process/machine-label')}
          />
        )}
      </div>
    </V1Screen>
  )
}
